import { Plus, X } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useNotify, useRefresh } from "ra-core";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import type { CrmDataProvider } from "../providers/types";
import { type PickedRecord, RecordPicker } from "./RecordPicker";

const SELECT_CLASS =
  "border-input h-9 rounded-md border bg-transparent px-3 text-sm shadow-xs";

/** "co_borrower" -> "Co borrower". */
const roleLabel = (role: string) =>
  role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, " ");

const ERRORS: Record<string, string> = {
  party_exists: "They are already on this deal in that role",
  primary_taken: "That role already has a primary; end it first",
  contact_merged: "That contact was merged; pick the surviving one",
};

/** End a party (W10 H3): they leave the deal but stay in its history. */
export function EndPartyButton({
  partyId,
  label,
}: {
  partyId: string;
  label: string;
}) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const refresh = useRefresh();
  const notify = useNotify();
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-7 text-muted-foreground hover:text-foreground"
      aria-label={`End ${label}`}
      onClick={async () => {
        try {
          await dataProvider.endParty(partyId);
          refresh();
        } catch {
          notify("The party could not be ended", { type: "error" });
        }
      }}
    >
      <X className="size-4" />
    </Button>
  );
}

/** Add a party to a deal (W10 H3): who, in which role, primary or not. */
export function AddPartyForm({ dealId }: { dealId: string }) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const refresh = useRefresh();
  const notify = useNotify();
  const [isOpen, setIsOpen] = useState(false);
  const [role, setRole] = useState("borrower");
  const [isPrimary, setIsPrimary] = useState(false);
  const [contact, setContact] = useState<PickedRecord | null>(null);
  const catalogs = useQuery({
    queryKey: ["catalogs"],
    queryFn: () => dataProvider.getCatalogs(),
    enabled: isOpen,
  });

  const reset = () => {
    setIsOpen(false);
    setContact(null);
    setIsPrimary(false);
  };

  if (!isOpen) {
    return (
      <Button
        className="mt-3 -ml-2 text-muted-foreground"
        size="sm"
        variant="ghost"
        onClick={() => setIsOpen(true)}
      >
        <Plus className="size-4" />
        Add party
      </Button>
    );
  }
  return (
    <form
      aria-label="Add party"
      className="mt-3 max-w-md space-y-3"
      onSubmit={async (event) => {
        event.preventDefault();
        if (!contact) return;
        try {
          await dataProvider.addParty(dealId, {
            contact_id: contact.id,
            role,
            is_primary: isPrimary,
          });
          reset();
          refresh();
        } catch (error) {
          const code =
            (error as { body?: { error?: string } }).body?.error ?? "";
          notify(ERRORS[code] ?? "The party could not be added", {
            type: "error",
          });
        }
      }}
    >
      <div className="space-y-1">
        <Label htmlFor="party-role">Role</Label>
        <select
          id="party-role"
          className={SELECT_CLASS}
          value={role}
          onChange={(e) => setRole(e.target.value)}
        >
          {(catalogs.data?.deal_party_roles ?? []).map((id) => (
            <option key={id} value={id}>
              {roleLabel(id)}
            </option>
          ))}
        </select>
      </div>
      <RecordPicker
        id="party-contact"
        label="Contact"
        objectType="contact"
        picked={contact}
        onPick={setContact}
        pickedText="Adding"
      />
      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={isPrimary}
          onCheckedChange={(checked) => setIsPrimary(checked === true)}
        />
        Primary in this role
      </label>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!contact}>
          Save party
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={reset}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
